import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import Card from '@/components/Card';
import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from 'react';
import {
  ExpandButton,
  HopCard,
  HopStackColumn,
  HopStackRow,
  IpStack,
  TracerouteButton,
} from './style';
import { ChevronDown, LucidePlay, Map } from 'lucide-react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { StatsContainer } from '../Latency/styles';
import InfoCard from '../InfoCard';
import RouteStep from '../RouteStep';
import { enqueueSnackbar } from 'notistack';
import TextField from '@/components/Input/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import apiFindIp from '@/services/apiFindIp';
import { useLanguage } from '@/context/LanguageContext';
import { isValidHost } from '@/utils/validators';
import { useStatsChartInfo } from './StatsChartInfo';
import { getHopColor } from '@/utils/getHopColor';
import { tracerouteAddress, ipv4Regex } from '@/utils/regex';

const TracerouteMap = lazy(() => import('@components/MapboxMap/TracerouteMap'));

type DataType = {
  address: string;
  latency: string;
  hop: number;
};

type HopType = {
  address: string;
  name: string | null;
  latency: string[];
  hop: number;
  lat?: number;
  lon?: number;
  city?: string;
  isp?: string;
  subdivIso?: string;
};

function Traceroute() {
  const { t } = useTranslation();
  const [ip, setIp] = useState<string>('');
  const [traceStarted, setTraceStarted] = useState<boolean>(false);
  const { language } = useLanguage();

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));

  const [eventSource, setEventSource] = useState<EventSource | null>(null);

  const [route, setRoute] = useState<HopType[]>([]);

  const [expanded, setExpanded] = useState<boolean>(false);
  const [mapped, setMapped] = useState<boolean>(false);
  const [mapping, setMapping] = useState<boolean>(false);
  const [ipError, setIpError] = useState<boolean>(false);

  // if the screen is small, expand by default
  useEffect(() => {
    if (isSm) {
      setExpanded(true);
    }
  }, [isSm]);

  const handleTrace = () => {
    setRoute([]);
    setTraceStarted(true);
    setMapped(false);
  };

  const handleData = (data: DataType) => {
    const hop = data.hop;
    const match = data.address.match(tracerouteAddress);
    let address = '';
    let name = null;
    if (match) {
      name = match[1].trim();
      address = match[2];
    } else {
      name = null;
      address = data.address;
    }
    const latency = data.latency.split('/').map((s) => s.trim());
    return { address: address, name: name, latency: latency, hop: hop };
  };

  // get the traceroute data via SSE
  useEffect(() => {
    if (!ip || !traceStarted) {
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
      }
      return;
    }

    const newEventSource = new EventSource(`api/notify/traceroute/${ip}`);

    // handle ip adress and latency data
    newEventSource.addEventListener('Traceroute', (e) => {
      const data = JSON.parse(e.data);
      const newEntry = {
        address: data.address,
        latency: data.latency,
        hop: data.hop,
      };
      console.log('New trace entry:', handleData(newEntry));
      setRoute((prevRoute) => [...prevRoute, handleData(newEntry)]);
    });

    // handle finished event
    newEventSource.addEventListener('Finished', (e) => {
      const data = e.data;
      console.log('Trace finished:', data);
      setTraceStarted(false);
      newEventSource.close();
      setEventSource(null);
      enqueueSnackbar(t('tools.traceroute.completed'), { variant: 'success' });
    });

    newEventSource.onerror = (err) => {
      console.error('EventSource error, closing connection.', err);
      newEventSource.close();
      setEventSource(null);
      setTraceStarted(false);
    };

    setEventSource(newEventSource);

    const handlePageHide = () => {
      newEventSource.close();
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      newEventSource.close();
      setEventSource(null);
    };
  }, [ip, traceStarted]);

  // handle statistics calculation
  const stats = useMemo(() => {
    if (route.length === 0)
      return {
        min: 'N/A',
        max: 'N/A',
        avg: 'N/A',
        successes: 0,
        failures: 0,
        percent: 'N/A',
      };

    const latencies: number[] = route
      .flatMap((hop) =>
        hop.latency
          .filter((lat) => lat !== '*')
          .map((lat) => parseFloat(lat.replace(/[^0-9.]/g, '')))
      )
      .filter((lat) => !Number.isNaN(lat));
    const total = latencies.reduce((acc, val) => acc + val, 0);
    const successes = latencies.length;
    const failures = route.reduce(
      (acc, hop) => acc + hop.latency.filter((lat) => lat === '*').length,
      0
    );
    const min = Math.min(...latencies);
    const max = Math.max(...latencies);
    const avg = successes > 0 ? total / successes : 0;
    const percent =
      successes + failures > 0 ? (successes / (successes + failures)) * 100 : 0;

    return {
      min: `${min}`,
      max: `${max}`,
      avg: `${avg.toFixed(2)}`,
      successes: successes,
      failures: failures,
      percent: `${percent.toFixed(1)}%`,
    };
  }, [route, traceStarted]);

  const StatsChartInfo = useStatsChartInfo(stats);

  const getUnit = (value: string | number) =>
    value === stats.successes ||
    value === stats.failures ||
    value === stats.percent
      ? ''
      : 'ms';

  // fetch location data for each hop using apiFindIp
  const getLocation = async () => {
    const updatedRoute = await Promise.all(
      route.map(async (hop) => {
        if (hop.address.match(ipv4Regex))
          try {
            const response = await apiFindIp(`${hop.address}`);
            const data = response.data;
            if (data != null)
              return {
                ...hop,
                lat: data.location.latitude,
                lon: data.location.longitude,
                city:
                  language === 'pt'
                    ? data.city.names['pt-BR']
                    : data.city.names[language] || data.city.names['en'],
                isp: data.traits.isp,
                subdivIso: data.subdivisions[0].iso_code,
              };
            return hop;
          } catch (error) {
            console.error('Error fetching location:', error);
            return hop;
          }
        return hop;
      })
    );
    console.log('Updated Route with Locations:', updatedRoute);
    setRoute(updatedRoute);
  };

  // fetch location data when mapping is toggled on
  const handleMap = async () => {
    setMapping(true);
    if (!mapped) {
      await getLocation();
    }
    setMapped((prevMapped) => !prevMapped);
    setMapping(false);
  };

  const handleIpChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setIp(value);

    if (value && !isValidHost(value)) {
      setIpError(true);
    } else {
      setIpError(false);
    }
  };

  return (
    <Stack direction="column" width="100%" spacing={2}>
      {/** Header card to get IP address input and control buttons */}
      <Card sx={{ maxWidth: '100%' }}>
        <Typography variant="h3">{t('tools.traceroute.title')}</Typography>
        <Typography variant="caption">
          {t('tools.traceroute.description')}
        </Typography>
        <IpStack $isSm={isMd}>
          <TextField
            value={ip}
            onChange={(e) => handleIpChange(e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && ip && !ipError && !traceStarted) {
                handleTrace();
              }
            }}
            placeholder={t('tools.traceroute.placeholder')}
            width={isMd ? '100%' : '400px'}
            sx={{ maxHeight: '38px' }}
            disabled={traceStarted}
            error={ipError}
            helperText={
              ipError ? t('equipment.latency.invalidHost') : undefined
            }
          />
          <TracerouteButton
            startIcon={
              traceStarted ? (
                <CircularProgress size="14px" />
              ) : (
                <LucidePlay size="14px" />
              )
            }
            variant="contained"
            disabled={!ip || traceStarted || ipError}
            onClick={handleTrace}
          >
            {traceStarted
              ? t('tools.traceroute.ongoing')
              : t('tools.traceroute.start')}
          </TracerouteButton>

          <TracerouteButton
            variant="text"
            startIcon={
              mapping ? <CircularProgress size="14px" /> : <Map size="14px" />
            }
            onClick={handleMap}
            disabled={route.length === 0 || traceStarted || mapping}
          >
            {mapped
              ? t('tools.traceroute.hideMap')
              : t('tools.traceroute.showMap')}
          </TracerouteButton>
        </IpStack>
      </Card>
      <Stack>
        <StatsContainer>
          {/** Shows statistics cards recalculating on each hop */}
          {StatsChartInfo.map((stat) => {
            return (
              <InfoCard
                key={stat.id}
                icon={stat.name}
                color={stat.color}
                title={stat.title}
                value={stat.value}
                unit={getUnit(stat.value)}
              />
            );
          })}
          <div />
        </StatsContainer>

        <HopCard $expanded={expanded}>
          {/** Exhibits each hop in the traceroute adjusting color on the ip values */}
          {expanded ? (
            <HopStackColumn>
              <RouteStep expanded={expanded} route={route} />
            </HopStackColumn>
          ) : (
            <HopStackRow>
              <RouteStep expanded={expanded} route={route} />
            </HopStackRow>
          )}
          <ExpandButton
            $expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            $isSm={isSm}
          >
            <ChevronDown />
          </ExpandButton>
        </HopCard>
      </Stack>

      {mapped && (
        <Card sx={{ height: '700px', width: '100%', padding: 0 }}>
          <Stack sx={{ height: '100%', width: '100%' }}>
            <Suspense fallback={<p>{t('common.loading')}</p>}>
              {/** Renders the map with hop geolocations */}
              <TracerouteMap
                locales={route
                  .filter(
                    (hop) => hop.lat !== undefined && hop.lon !== undefined
                  )
                  .map((hop) => ({
                    lat: hop.lat!,
                    lon: hop.lon!,
                    city: hop.city || 'Unknown',
                    isp: hop.isp || 'Unknown',
                    color: getHopColor(hop.latency, theme),
                    hop: hop.hop,
                    subdiv: hop.subdivIso || '',
                  }))}
                showRoute={true}
              />
            </Suspense>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

export default Traceroute;
